import * as React from "react";
import { usePathname, useSearch as useSearchParams } from "../../router";

import { cn } from "../../utils";
import { INTENT_CLASSES } from "../../tokens";
import { NavTabs, type NavTab } from "../../nav-tabs";
import type { TaskStatus } from "../../types/projects";
import { STATUS_SEGMENTS } from "./tasks-state";

export interface IssuesStatusPillsProps {
  /** Per-status task counts for the current scope — the count badge on each tab. */
  counts: Record<TaskStatus, number>;
  /**
   * The active segment key (`open` · `in_progress` · `review` · `done`), resolved
   * on the SERVER from the effective `?status=` set (the mode mount injects the
   * "Open" default on a bare landing, so the URL alone can't tell — the producer
   * passes the resolved answer). `null` when a custom filter matches no segment.
   */
  activeKey: string | null;
  className?: string;
}

/**
 * The Issues section's primary navigation — Open · In progress · Needs review ·
 * Done — as the canonical `NavTabs` pill row (Matt, 2026-07-23: "these all need
 * to be navigation tabs, but they can keep the number and the status icon"). Each
 * tab is a `<Link>` that sets `?status=` and drops back to all-time, so picking a
 * segment sorts the list beneath it; other query params (assignee, project, dates,
 * the view path) carry through untouched.
 *
 * It renders through `NavTabs` rather than a hand-rolled `<Link>` row so the look
 * is the ONE tab treatment the product uses everywhere (rounded pills, only the
 * active tab filled) — the status dot rides the tab's `icon` slot and the count
 * its `count` slot. Selection is URL state, but the active tab comes from the
 * server-resolved `activeKey` (passed as NavTabs' `value`, matched against each
 * tab's `activeHref`), not `useSearchParams`, because the landing default lives in
 * the mount, not the URL.
 */
export function IssuesStatusPills({ counts, activeKey, className }: IssuesStatusPillsProps) {
  const pathname = usePathname();
  const sp = useSearchParams();

  const hrefFor = React.useCallback(
    (statuses: TaskStatus[]): string => {
      const next = new URLSearchParams(sp?.toString());
      next.set("status", statuses.join(","));
      // A status pick is an all-time view of that bucket (the segment IS the
      // filter), and it supersedes any section preset — clear both so the URL
      // never disagrees with the rows on screen.
      next.set("range", "all");
      next.delete("section");
      const qs = next.toString();
      return qs ? `${pathname}?${qs}` : pathname;
    },
    [pathname, sp],
  );

  const tabs: NavTab[] = STATUS_SEGMENTS.map((seg) => ({
    href: hrefFor(seg.statuses),
    // Light the active tab off the SERVER-resolved segment key (via `value`
    // below), not the URL — so a bare landing correctly shows "Open" as active
    // even though the query string carries no `?status=` yet.
    activeHref: seg.key,
    label: seg.label,
    count: seg.statuses.reduce((n, s) => n + (counts[s] ?? 0), 0),
    icon: <span aria-hidden className={cn("size-2 rounded-full", INTENT_CLASSES[seg.intent].dot)} />,
  }));

  // `value` wins over NavTabs' own URL matching (the documented external-state
  // case). `null` (a custom multi-status filter) → "", which equals no tab's
  // `activeHref`, so nothing lights — exactly the "off a segment" state.
  return <NavTabs tabs={tabs} value={activeKey ?? ""} className={cn("flex-wrap", className)} />;
}
